import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertTriggers,
  convertQuestionMappings,
  convertNotRequired,
} from '../utils/convertVisibleIf'

const BIRTH_DATE_PLACEHOLDER = ['$', '{A:birthday}'].join('')

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
  birthDate,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
    // useUserIdLogin: true,
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
    21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39,
    40,
  ]
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
              console.log('✅ 強制加上 mobile class 避免抖動')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 以下皆為 1-based 寫法，convertVisibleIf / 字串插值會由工具自動 -1 還原為 0-based
  // q8/q12/q13 都是民國年次選項；選項 16-47 對應民國 64-95 年次。
  const isBirthYear64To95 =
    '({q8} >= 16 and {q8} <= 47) or ({q12} >= 16 and {q12} <= 47) or ({q13} >= 16 and {q13} <= 47)'
  const isWantsPregnant =
    '(' +
    isBirthYear64To95 +
    ') and ({q14} = 2) and ({q35} = 2 or {q35} = 3 or {q36} = 2) and ({q199} = 2 or {q199} = 3) and ({q202} = 4 or {q202} = 5 or {q202} = 6)'
  const isPostmenopausal =
    '(({q8} >= 6 and {q8} <= 25) or ({q12} >= 6 and {q12} <= 25) or ({q13} >= 6 and {q13} <= 25)) and ({q14} = 2) and ({q202} = 1)'
  const isRecentDrinker = '({q275} = 1 or {q275} = 2)'

  const visibleIfConditionsInit = convertVisibleIf({
    // A1_1(q5)=2(不是) → 顯示 A1_0a(q6)
    q6: '{q5} = 2',
    q7: '{q6} = 1',
    q8: '({q6} = 1)',
    q9: '({q6} = 1)',
    q10: '({q6} = 1)',
    q11: '({q6} = 2)',
    // A1_1(q5)=1(是)/3(不清楚) 也直接顯示 A1_1b-1(q12)
    q12: '({q11} = 1) or ({q5} = 1) or ({q5} = 3)',
    q13: '({q11} = 2)',
    q16: '{q15} = 8 or {q15} = 11 or {q15} = 14 or {q15} = 16 or {q15} = 18 or {q15} = 19',
    q18: '{q17} = 1',
    q19: '{q17} = 2',
    // A5
    q21: '{q20} = 1',
    q22: '{q20} = 1',
    q23: '{q20} = 1',
    q24: '{q20} = 1',
    q25: '{q20} = 1',
    q26: '{q20} = 1',
    q27: '{q20} = 1',
    q28: '{q20} = 1',
    q29: '{q20} = 1',
    q30: '{q20} = 1',
    q31: '{q20} = 1',
    q32: '{q20} = 1',
    q33: '{q20} = 1',
    q34: '{q20} = 1',
    // A6
    q36: '{q35} = 1 or {q35} = 4 or {q35} = 5 or {q35} = 6 or {q35} = 7',
    q37: '{q35} = 2 or {q35} = 3 or {q36} = 2',
    q38: '({q35} = 2 or {q35} = 3 or {q36} = 2) and ({q37} = 1)',
    q39: '({q35} = 2 or {q35} = 3 or {q36} = 2) and ({q37} = 2 or {q37} = 3)',
    q40: '({q35} = 2 or {q35} = 3 or {q36} = 2) and ({q37} = 1 or {q37} = 2 or {q37} = 3)',
    q41: '{q35} = 2 or {q35} = 3 or {q36} = 2',
    q42: '{q41} = 8 or {q41} = 11 or {q41} = 14 or {q41} = 16 or {q41} = 18 or {q41} = 19',
    // A7
    q44: '{q43} = 1',
    q45: '{q43} = 2 or {q43} = 3',
    q47: '{q46} = 1',
    q48: '{q46} = 2 or {q46} = 3',
    // B3
    q52: '{q51} = 2',
    q53: '({q51} = 2) and ({q52} = 2)',
    q54: '({q51} = 2) and ({q52} = 2) and ({q53} = 2)',
    q55: '{q51} = 2',
    // B4
    q57: '{q56} = 2',
    q58: '({q56} = 2) and ({q57} = 2)',
    q59: '({q56} = 2) and ({q57} = 2)',
    q60: '({q56} = 2) and ({q57} = 2) and ({q59} = 2)',
    q61: '{q56} = 2',
    q62: '{q56} = 2',
    q63: '{q56} = 2',
    q64: '({q56} = 2) and ({q63} = 2)',
    q65: '{q56} = 2',
    q66: '{q56} = 2',
    q67: '({q56} = 2) and ({q66} = 2)',
    q68: '{q56} = 2',
    q69: '({q56} = 2) and ({q68} = 2)',
    // B5
    q71: '{q70} = 2',
    q72: '({q70} = 2) and ({q71} = 2)',
    q73: '({q70} = 2) and ({q71} = 2) and ({q72} = 2)',
    q74: '{q70} = 2',
    // B6
    q75: '({q51} = 2) or ({q56} = 2) or ({q70} = 2)',
    // B7 中風
    q77: '{q76} = 2',
    q78: '{q76} = 2',
    q79: '{q76} = 2',
    q80: '({q76} = 2) and ({q79} = 1)',
    q81: '({q76} = 2) and ({q79} = 1)',
    q82: '({q76} = 2) and ({q79} = 2)',
    q83: '{q76} = 2',
    q84: '({q76} = 2) and ({q83} = 2)',
    // B8 氣喘
    q86: '{q85} = 2',
    q87: '({q85} = 2) and ({q86} = 2)',
    q88: '({q85} = 2) and ({q86} = 2) and ({q87} = 3)',
    q89: '({q85} = 2) and ({q86} = 2)',
    q90: '({q85} = 2) and ({q86} = 2) and ({q89} = 2)',
    q91: '({q85} = 2) and ({q86} = 2)',
    q92: '({q85} = 2) and ({q86} = 2) and ({q91} = 2)',
    q93: '({q85} = 2) and ({q86} = 2)',
    q94: '({q85} = 2) and ({q86} = 2) and ({q93} = 2)',
    q95: '({q85} = 2) and ({q86} = 2)',
    q96: '({q85} = 2) and ({q86} = 2) and ({q95} = 2)',
    q97: '({q85} = 2) and ({q86} = 2)',
    q98: '({q85} = 2) and ({q86} = 2)',
    // B9 COPD
    q104: '{q103} = 2',
    // B10 B/C 肝炎
    q106: '{q105} = 2',
    q107: '{q105} = 2',
    q108: '({q105} = 2) and ({q107} = 2)',
    q109: '({q105} = 2) and ({q107} = 2) and ({q108} = 1)',
    q110: '({q105} = 2) and ({q107} = 2) and ({q108} = 2)',
    q111: '({q105} = 2) and ({q107} = 2) and ({q108} = 1 or {q108} = 2)',
    q113: '{q112} = 2',
    q114: '{q112} = 2',
    q115: '({q112} = 2) and ({q114} = 2)',
    q116: '({q112} = 2) and ({q114} = 2) and ({q115} = 1)',
    q117: '({q112} = 2) and ({q114} = 2) and ({q115} = 2)',
    q118: '({q112} = 2) and ({q114} = 2) and ({q115} = 1 or {q115} = 2)',
    q119: '({q112} = 2) and ({q114} = 2) and ({q115} = 2)',
    // B11 腎臟病
    q124: '{q123} = 2',
    q125: '{q123} = 2',
    q126: '({q123} = 2) and ({q125} = 2)',
    q127: '{q123} = 2',
    // B12 6/23 改為逐項病症題，選「有」才顯示該病症後續題。
    q129: '{q128} = 2',
    q130: '{q128} = 2',
    q131: '{q128} = 2',
    q133: '{q132} = 2',
    q134: '{q132} = 2',
    q135: '{q132} = 2',
    q137: '{q136} = 2',
    q138: '{q136} = 2',
    q139: '{q136} = 2',
    q141: '{q140} = 2',
    q142: '{q140} = 2',
    q143: '{q140} = 2',
    q145: '{q144} = 2',
    q146: '{q144} = 2',
    q148: '{q147} = 2',
    q149: '{q147} = 2',
    q150: '{q147} = 2',
    q151: '{q147} = 2',
    q152: '{q147} = 2',
    q153: '{q152} = 2',
    q154: '{q152} = 2',
    q155: '{q152} = 2',
    q156: '{q152} = 2',
    q158: '{q157} = 2',
    q159: '{q157} = 2',
    q160: '{q157} = 2',
    q162: '{q161} = 2',
    q163: '{q161} = 2',
    q164: '{q161} = 2',
    q166: '{q165} = 2',
    q167: '{q165} = 2',
    q168: '{q165} = 2',
    q170: '{q169} = 2',
    q171: '{q169} = 2',
    q172: '{q169} = 2',
    q173: '{q14} = 1 or {q14} = 3',
    q174: '({q14} = 1 or {q14} = 3) and ({q173} = 2)',
    q175: '({q14} = 1 or {q14} = 3) and ({q173} = 2)',
    q176: '({q14} = 1 or {q14} = 3) and ({q173} = 2)',
    q177: '{q14} = 2',
    q178: '({q14} = 2) and ({q177} = 2)',
    q179: '({q14} = 2) and ({q177} = 2)',
    q180: '({q14} = 2) and ({q177} = 2)',
    // B13
    q184: '{q183} = 1',
    q185: '{q183} = 2 or {q183} = 3 or {q183} = 4',
    q186: '({q184} = 2 or {q184} = 3 or {q184} = 4) or ({q185} = 2 or {q185} = 3 or {q185} = 4)',
    q187: '{q186} = 2',
    // B14 牙齒
    q189: '{q188} = 2',
    q190: '{q188} = 2 or {q188} = 3',
    q191: '{q190} anyof [2]',
    q192: '({q190} anyof [3]) or ({q191} = 1)',
    // B15 生育子女數
    q195: '({q8} >= 16 and {q8} <= 47) or ({q12} >= 16 and {q12} <= 47) or ({q13} >= 16 and {q13} <= 47)',
    q196: '{q195} = 2',
    q197: '{q195} = 2',
    q198: '{q195} = 2',
    q199: isBirthYear64To95,
    q200: '(' + isBirthYear64To95 + ') and ({q199} = 2)',
    q201: '(' + isBirthYear64To95 + ') and ({q199} = 2)',
    // B16-B18
    q202: '{q14} = 2',
    q203: isWantsPregnant,
    q204: isWantsPregnant + ' and ({q203} = 2 or {q203} = 3)',
    q205: isWantsPregnant + ' and ({q203} = 2 or {q203} = 3) and ({q204} = 2)',
    q206: isWantsPregnant + ' and ({q203} = 2 or {q203} = 3) and ({q204} = 1)',
    q207:
      isWantsPregnant +
      ' and (({q203} = 1) or ({q205} anyof [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]) or ({q206} anyof [1, 2, 3, 4, 5, 6, 7, 8, 9]))',
    q208: isPostmenopausal,
    q209: isPostmenopausal,
    q210: isPostmenopausal + ' and ({q209} = 2)',
    q211: isPostmenopausal + ' and ({q209} = 2)',
    q212: isPostmenopausal + ' and ({q209} = 2) and ({q211} = 2)',
    q213: isPostmenopausal + ' and ({q209} = 2)',
    q214: isPostmenopausal + ' and ({q209} = 2) and ({q213} = 2)',
    q215: isPostmenopausal + ' and ({q209} = 1 or {q209} = 2 or {q209} = 4)',
    q216: isPostmenopausal + ' and ({q209} = 1 or {q209} = 2 or {q209} = 4)',
    q217: isPostmenopausal + ' and ({q209} = 1 or {q209} = 2 or {q209} = 4)',
    // B19
    q219: '{q218} = 1',
    q220: '{q218} = 2 or {q218} = 3 or {q218} = 4',
    // C
    q226: '{q225} = 3',
    q234: '{q233} = 2',
    q236: '{q235} = 2',
    q238: '{q237} = 2',
    q240: '{q239} = 2',
    q242: '(({q8} >= 1 and {q8} <= 25) or ({q12} >= 1 and {q12} <= 25) or ({q13} >= 1 and {q13} <= 25)) and {q241} = 2',
    q243: '{q241} = 2 and {q242} = 1',
    q245: '{q244} = 2',
    q246: '{q244} = 1',
    q248: '{q247} = 2',
    q249: '{q247} = 2',
    q251: '{q250} = 2',
    q252: '(({q8} >= 21 and {q8} <= 25) or ({q12} >= 21 and {q12} <= 25) or ({q13} >= 21 and {q13} <= 25)) and {q14} = 2',
    q253: '((({q8} >= 1 and {q8} <= 20) or ({q12} >= 1 and {q12} <= 20) or ({q13} >= 1 and {q13} <= 20)) and {q14} = 2) or ({q252} = 2)',
    q254: '{q253} = 2',
    q255: '({q253} = 1) or ({q254} = 3 or {q254} = 4 or {q254} = 5)',
    q256: '({q253} = 1 or {q253} = 3 or {q253} = 4 or {q253} = 5) or ({q254} = 3 or {q254} = 4 or {q254} = 5)',
    q257: '{q256} = 2',
    q258: '({q8} >= 20 and {q8} <= 47) or ({q12} >= 20 and {q12} <= 47) or ({q13} >= 20 and {q13} <= 47)',
    q259: '(({q8} >= 1 and {q8} <= 35) or ({q12} >= 1 and {q12} <= 35) or ({q13} >= 1 and {q13} <= 35)) and {q14} = 2',
    q260: '{q259} = 2',
    q261: '{q259} = 1 or {q260} = 4 or {q260} = 5',
    q262: '(({q8} >= 1 and {q8} <= 35) or ({q12} >= 1 and {q12} <= 35) or ({q13} >= 1 and {q13} <= 35)) and {q14} = 2',
    q263: '{q262} = 2',
    q264: '({q8} >= 1 and {q8} <= 15) or ({q12} >= 1 and {q12} <= 15) or ({q13} >= 1 and {q13} <= 15)',
    q265: '{q264} = 2',
    q266: '{q264} = 1',
    q267: '({q8} >= 1 and {q8} <= 25) or ({q12} >= 1 and {q12} <= 25) or ({q13} >= 1 and {q13} <= 25)',
    q268: '{q267} = 2',
    q269: '({q8} >= 1 and {q8} <= 45) or ({q12} >= 1 and {q12} <= 45) or ({q13} >= 1 and {q13} <= 45)',
    q270: '{q269} = 2',
    q271: '{q269} = 2',
    q272: '{q269} = 2',
    // D1 喝酒
    q274: '{q273} = 2',
    q275: '{q273} = 2',
    q276: '({q273} = 2) and ({q275} = 1 or {q275} = 2 or {q275} = 3 or {q275} = 4)',
    // D1c 過去一個月飲酒；第 2~5 輪由前一輪「還有其他」觸發。
    q277: isRecentDrinker,
    q278: isRecentDrinker,
    q279: isRecentDrinker,
    q280: isRecentDrinker,
    q281: isRecentDrinker + ' and ({q280} = 2)',
    q282: isRecentDrinker + ' and ({q280} = 2)',
    q283: isRecentDrinker + ' and ({q280} = 2)',
    q284: isRecentDrinker + ' and ({q280} = 2)',
    q285: isRecentDrinker + ' and ({q284} = 2)',
    q286: isRecentDrinker + ' and ({q284} = 2)',
    q287: isRecentDrinker + ' and ({q284} = 2)',
    q288: isRecentDrinker + ' and ({q284} = 2)',
    q289: isRecentDrinker + ' and ({q288} = 2)',
    q290: isRecentDrinker + ' and ({q288} = 2)',
    q291: isRecentDrinker + ' and ({q288} = 2)',
    q292: isRecentDrinker + ' and ({q288} = 2)',
    q293: isRecentDrinker + ' and ({q292} = 2)',
    q294: isRecentDrinker + ' and ({q292} = 2)',
    q295: isRecentDrinker + ' and ({q292} = 2)',
    q296: isRecentDrinker + ' and ({q292} = 2)',
    // D1d 過去一個月喝最多的一次。
    q297: isRecentDrinker,
    q298: isRecentDrinker,
    q299: isRecentDrinker,
    q300: isRecentDrinker,
    q301: isRecentDrinker + ' and ({q300} = 2)',
    q302: isRecentDrinker + ' and ({q300} = 2)',
    q303: isRecentDrinker + ' and ({q300} = 2)',
    q304: isRecentDrinker + ' and ({q300} = 2)',
    q305: isRecentDrinker + ' and ({q304} = 2)',
    q306: isRecentDrinker + ' and ({q304} = 2)',
    q307: isRecentDrinker + ' and ({q304} = 2)',
    q308: isRecentDrinker + ' and ({q304} = 2)',
    q309: isRecentDrinker + ' and ({q308} = 2)',
    q310: isRecentDrinker + ' and ({q308} = 2)',
    q311: isRecentDrinker + ' and ({q308} = 2)',
    q312: isRecentDrinker + ' and ({q308} = 2)',
    q313: isRecentDrinker + ' and ({q312} = 2)',
    q314: isRecentDrinker + ' and ({q312} = 2)',
    q315: isRecentDrinker + ' and ({q312} = 2)',
    q316: isRecentDrinker + ' and ({q312} = 2)',
    q317: isRecentDrinker,
    q318: '({q273} = 2) and ({q275} = 1 or {q275} = 2 or {q275} = 3 or {q275} = 4)',
    q319: '({q273} = 2) and ({q275} = 1 or {q275} = 2 or {q275} = 3 or {q275} = 4) and ({q318} = 2 or {q318} = 3)',
    q320: '({q273} = 2) and ({q275} = 1 or {q275} = 2 or {q275} = 3 or {q275} = 4)',
    q321: '({q273} = 2) and ({q320} = 2)',
    q322: '({q273} = 1) or (({q273} = 2) and ({q275} = 1 or {q275} = 2 or {q275} = 3 or {q275} = 4 or {q275} = 5))',
    // D2 吸菸
    q325: '{q324} = 2 or {q324} = 3',
    q326: '({q324} = 2 or {q324} = 3) and ({q325} = 2)',
    q327: '({q324} = 2 or {q324} = 3) and ({q325} = 1)',
    q328: '({q324} = 2 or {q324} = 3) and ({q325} = 2)',
    q329: '{q324} = 2 or {q324} = 3',
    q330: '({q324} = 2 or {q324} = 3) and ({q329} = 2)',
    q331: '({q324} = 2 or {q324} = 3) and ({q329} = 2)',
    q332: '{q331} = 2',
    // D2e 其他菸品
    q334: '{q333} = 2 or {q333} = 3',
    q335: '({q333} = 2 or {q333} = 3) and ({q334} = 2 or {q334} = 3)',
    q337: '{q336} = 2 or {q336} = 3',
    q338: '({q336} = 2 or {q336} = 3) and ({q337} = 2 or {q337} = 3)',
    q340: '{q339} = 2 or {q339} = 3',
    q341: '({q339} = 2 or {q339} = 3) and ({q340} = 2 or {q340} = 3)',
    q343: '{q342} = 2 or {q342} = 3',
    q344: '({q342} = 2 or {q342} = 3) and ({q343} = 2 or {q343} = 3)',
    q346: '{q345} = 2 or {q345} = 3',
    q347: '({q345} = 2 or {q345} = 3) and ({q346} = 2 or {q346} = 3)',
    q349: '{q348} = 2 or {q348} = 3',
    q350: '({q348} = 2 or {q348} = 3) and ({q349} = 2 or {q349} = 3)',
    q352: '{q351} = 2 or {q351} = 3',
    q353: '{q351} = 2 or {q351} = 3',
    q354: '({q351} = 2 or {q351} = 3) and ({q353} = 2 or {q353} = 3)',
    // D3 二手菸
    q356: '{q355} = 2',
    q357: '({q355} = 2) and ({q356} = 2)',
    q358: '{q355} = 2',
    q359: '{q355} = 2',
    q360: '{q355} = 2',
    q361: '{q355} = 2',
    q362: '{q355} = 2',
    // D4 嚼檳榔
    q364: '{q363} = 3',
    q365: '({q363} = 3) and ({q364} = 1)',
    q366: '({q363} = 3) and ({q364} = 1)',
    q367: '({q363} = 3) and ({q364} = 1)',
    q368: '({q363} = 3) and ({q364} = 1) and ({q367} = 2)',
    q369: '({q363} = 3) and ({q364} = 1)',
    q370: '({q363} = 3) and ({q364} = 1)',
    q371: '({q363} = 3) and ({q364} = 2)',
    q372: '({q363} = 3) and ({q364} = 2) and ({q371} = 2 or {q371} = 3 or {q371} = 4 or {q371} = 5)',
    q373: '({q363} = 3) and ({q364} = 2) and ({q371} = 2 or {q371} = 3 or {q371} = 4 or {q371} = 5) and ({q372} = 1)',
    q374: '({q363} = 3) and ({q364} = 2) and ({q371} = 2 or {q371} = 3 or {q371} = 4 or {q371} = 5) and ({q372} = 2)',
    q375: '({q363} = 3) and ({q364} = 2)',
    q376: '({q363} = 3) and ({q364} = 2)',
    q377: '({q363} = 3) and ({q364} = 2) and ({q376} = 2)',
    q378: '({q363} = 3) and ({q364} = 2)',
    q379: '({q363} = 3) and ({q364} = 2)',
    q380: '({q363} = 3) and ({q364} = 2) and ({q379} = 2)',
    q381: '({q363} = 3) and ({q364} = 2) and ({q379} = 2)',
    q382: '({q363} = 3) and ({q364} = 2) and ({q379} = 2)',
    q383: '({q363} = 3) and ({q364} = 2)',
    q384: '({q363} = 3) and ({q364} = 2)',
    // D5 口腔黏膜
    q387: '((({q8} >= 1 and {q8} <= 35) or ({q12} >= 1 and {q12} <= 35) or ({q13} >= 1 and {q13} <= 35)) and ({q324} = 3 or {q363} = 3)) or ((({q8} >= 35 and {q8} <= 47) or ({q12} >= 35 and {q12} <= 47) or ({q13} >= 35 and {q13} <= 47)) and ({q363} = 3) and ({q18} = 2))',
    q388: '{q387} = 2',
    q389: '({q387} = 1) or ({q387} = 2 and ({q388} = 3 or {q388} = 4 or {q388} = 5))',
    // D6-D8
    q391: '{q390} = 1',
    q392: '{q390} = 2',
    q394: '{q393} = 2',
    q399: '{q398} = 1 or {q398} = 2',
    q401: '{q400} = 2 or {q400} = 3 or {q400} = 4 or {q400} = 5 or {q400} = 6 or {q400} = 7 or {q400} = 8',
    q403: '{q402} = 2 or {q402} = 3 or {q402} = 4 or {q402} = 5 or {q402} = 6 or {q402} = 7 or {q402} = 8',
    // D10 身體活動
    q406: '{q405} = 2',
    q407: '{q405} = 2',
    q408: '{q405} = 2',
    q410: '{q409} = 2',
    q411: '{q409} = 2',
    q412: '{q409} = 2',
    q414: '{q413} = 2',
    q415: '{q414} = 2 or {q414} = 3 or {q414} = 4 or {q414} = 5 or {q414} = 6 or {q414} = 7 or {q414} = 8',
    q416: '{q414} = 2 or {q414} = 3 or {q414} = 4 or {q414} = 5 or {q414} = 6 or {q414} = 7 or {q414} = 8',
    q417: '{q413} = 2',
    q418: '{q417} = 2 or {q417} = 3 or {q417} = 4 or {q417} = 5 or {q417} = 6 or {q417} = 7 or {q417} = 8',
    q419: '{q417} = 2 or {q417} = 3 or {q417} = 4 or {q417} = 5 or {q417} = 6 or {q417} = 7 or {q417} = 8',
    q421: '{q420} = 2',
    q422: '{q420} = 2',
    q423: '{q420} = 2',
    q425: '{q424} = 2',
    q426: '{q424} = 2',
    q427: '{q424} = 2',
    q429: '{q428} < 25',
    // D12 咳嗽發燒
    q431: '{q430} = 2',
    q432: '{q430} = 2',
    q433: '({q430} = 2) and ({q432} = 1 or {q432} = 2 or {q432} = 3 or {q432} = 4 or {q432} = 6)',
    // G/I
    q446: '{q445} = 1',
    q447: '{q445} = 2',
    q448: '{q445} = 3',
    q449: '{q445} = 3',
    q450: '{q445} = 3',
    q451: '{q445} = 3',
    q452: '{q445} = 3',
    q453: '({q445} = 3) and ({q452} = 1)',
    q454: '({q445} = 3) and ({q452} = 2 or {q452} = 4)',
    q455: '{q445} = 3',
    q457: '{q456} = 1 or {q456} = 3 or {q456} = 4',
    q461: '{q460} = 2',
    q465: '{q464} = 1 or {q464} = 2 or {q464} = "other"',
    q471: '{q470} notcontains ["none"]',
    q472: '{q471} = 2',
    q473: '{q471} = 2',
  })

  // 非必填（1-based 寫法，convertNotRequired 會 -1）
  const notRequired = convertNotRequired([
    52, 53, 54, 55, 57, 58, 59, 60, 61, 62, 63, 65, 66, 67, 68, 71, 72, 73, 74,
    77, 78, 79, 80, 81, 82, 83, 84, 96, 97, 98, 104, 110, 115, 117, 118, 183,
    184, 185, 186, 187, 189, 190, 191, 192, 193, 194, 196, 197, 198, 200, 201,
    207, 211, 212, 213, 254, 257, 260, 263, 265, 268, 272, 317, 318, 319, 320,
    321, 322, 323, 325, 326, 327, 328, 329, 330, 331, 332, 334, 335, 336, 337,
    338, 339, 340, 341, 342, 343, 344, 345, 346, 347, 348, 349, 350, 351, 352,
    353, 354, 356, 357, 358, 359, 360, 361, 362, 365, 366, 367, 368, 371, 372,
    373, 374, 375, 376, 377, 379, 380, 383, 388, 390, 391, 392, 393, 394, 395,
    396, 397, 401, 402, 404, 405, 407, 408, 410, 411, 412, 414, 415, 416, 417,
    418, 419, 421, 422, 423, 425, 426, 427, 437, 438, 439, 440, 441, 442, 443,
    448, 449, 450, 451, 452, 453, 454, 455, 458, 459,
  ])

  const mappedRateChoice = null
  const pageBreakQuestions = null

  const triggersConditionsInit = convertTriggers([])
  const questionMappings = convertQuestionMappings({})

  const transformSurveyData = useMemo(() => {
    if (!birthDate) return undefined
    const replacement = `${birthDate.rocYear}年${birthDate.month}月${birthDate.day}日`
    return (data) => ({
      ...data,
      data: (data.data || []).map((q) => ({
        ...q,
        ma: {
          ...q.ma,
          content:
            typeof q.ma?.content === 'string'
              ? q.ma.content.split(BIRTH_DATE_PLACEHOLDER).join(replacement)
              : q.ma?.content,
        },
      })),
    })
  }, [birthDate])

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    transformSurveyData,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  const endSurveyMsg = (survey) => {
    const host = process.env.REACT_APP_HOST
    const input = btoa(`ac=${ac}&tk=${tk}&pi=${pid}`)
    const pdfUrl = `${host}/clientWeb/v1/ExportProjectPdf?input=${input}`
    const endMessage = `<div>我們的訪問到這邊告一段落，非常謝謝您的協助！<br><br>♥國民健康署祝您健康快樂♥<br><br><br>相關健康資訊可至國民健康署網站查詢<br><br>(1)可選下方按鈕，自行留存「受訪者同意書」。<br><a href="${pdfUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:8px 16px;background-color:#4CAF50;color:#fff;text-decoration:none;border-radius:4px;margin-top:8px;">下載同意書留存</a><br><br>(2)<span style="color:#EA0000">好禮抽抽週活動</span>，請詳見活動說明網頁：<br>👉<a href="/P260601122243471/lottery" target="_blank">${window.location.origin}/P260601122243471/lottery</a></div>`
    setCompletionMessage(endMessage)
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving: requiresLogin.login || webAuthType > 0,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
